import {PhoneIntegrationCard} from 'shells/card/phone-integration-card';
import {colors} from 'shells/colors/colors';
import {GridList} from 'shells/grid-list/grid-list';
// @ts-expect-error - imported from .js/.jsx file
import {NutshellLogoMarkIcon} from 'shells/icon/svg/nutshell-logo-mark-icon';
import {Stack} from 'shells/layout/stack/stack';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {Body} from 'shells/typography/body';

import {appHistory} from '../../../history';
import {addNotification} from '../../../notifications/index';

import {useSetPhoneIntegrationIsEnabled} from '../../calls/graphql/hooks/use-set-phone-integration-is-enabled';
import {
    getServiceNoun,
    getServiceDescription,
    getServiceTypeIconComponent,
} from '../../calls/helpers';
import {SetupCard} from '../../calls/setup-card';
import {useGetSessionUser} from '../../core/use-get-session-user';
import {CueCardStack} from '../../glossary/layout/cue-card-stack';
import {useGetCueCardsForLocation} from '../../glossary/layout/helpers';
import {PageTitleBar} from '../../layout';

import {useDeleteCallrailIntegrationUserSettings} from './graphql/hooks/use-delete-callrail-integration-user-settings';
import {useDisableClickToCallIntegration} from './graphql/hooks/use-disable-click-to-call-integration';
import {PhoneIntegrationDefaultProviderSetting} from './phone-integration-default-provider-setting';

import styles from './phone-settings-page.module.css';

export function PhoneSettingsPage() {
    const {user, isLoading: isLoadingUser, refetch, errorMessage} = useGetSessionUser();

    const {getSetIsEnabledMutationByIntegration} = useSetPhoneIntegrationIsEnabled();
    const {deleteCallrailIntegrationUserSettings} = useDeleteCallrailIntegrationUserSettings();
    const {disableClickToCallIntegration} = useDisableClickToCallIntegration();

    const phoneIntegrations = user ? user.phoneIntegrations : [];
    const enabledPhoneIntegrations = phoneIntegrations.filter(
        (integration) => integration.isEnabledForUser
    );
    const disabledPhoneIntegrations = phoneIntegrations.filter(
        (integration) => !integration.isEnabledForUser
    );

    const clickToCallIntegration = user ? user.clickToCallIntegration : null;

    const hasEnabledRingcentral = enabledPhoneIntegrations.some(
        (integration) => integration.integrationType === 'RINGCENTRAL'
    );

    const cueCards = useGetCueCardsForLocation('/my-account/phone', 'settings-sidebar')?.filter(
        (card) =>
            hasEnabledRingcentral && NutClientConfig.hasRingcentralAppconnectFF
                ? card.settingsKey !== 'phone-integrations'
                : card.settingsKey !== 'ringcentral-appconnect'
    );

    const clickToCallIsEnabled = Boolean(
        clickToCallIntegration && clickToCallIntegration.isEnabled
    );
    const canUseClickToCall = NutCapabilities.clickToCall.isAvailable;
    const clickToCallTitle = `Nutshell ${getServiceNoun('CLICKTOCALL')}`;

    // @ts-expect-error
    const handleDisable = (integrationType) => {
        if (integrationType === 'CALLRAIL') {
            deleteCallrailIntegrationUserSettings().catch(() => {
                addNotification('Something went wrong', {
                    body: 'Failed to disable integration',
                    icon: 'frowny',
                });
            });
        } else {
            const setIsEnabledMutation = getSetIsEnabledMutationByIntegration(integrationType);

            if (setIsEnabledMutation) {
                // @ts-expect-error
                setIsEnabledMutation(false)
                    .catch(() => {
                        addNotification('Something went wrong', {
                            body: 'Failed to disable integration',
                            icon: 'frowny',
                        });
                    })
                    .then(() => {
                        refetch();
                    });
            }
        }
    };

    return (
        <>
            <PageTitleBar
                title='Phone settings'
                subheaderPrimaryText='Connect your phone system to Nutshell'
            />
            <div className='full-height full-width overflow-y-auto flex flex-dir-row'>
                <div className='flex flex-dir-col pad-16 gap-16 flex-grow height-fit-content'>
                    {(enabledPhoneIntegrations.length > 0 || clickToCallIsEnabled) && (
                        <Stack spacing={8}>
                            <PhoneIntegrationDefaultProviderSetting
                                showSavedToast={true}
                                onError={() => {}}
                                radioGroupName='configure default provider - settings tab'
                                clickToCallEnabled={Boolean(clickToCallIsEnabled)}
                                enabledPhoneIntegrations={enabledPhoneIntegrations}
                            />
                        </Stack>
                    )}
                    {clickToCallIsEnabled && clickToCallIntegration && (
                        <SetupCard
                            key={clickToCallIntegration.integrationType}
                            title={clickToCallTitle}
                            externalUserIdentifier={clickToCallIntegration.callerId ?? ''}
                            tertiaryInformation={clickToCallIntegration.phoneNumber ?? ''}
                            integrationType={clickToCallIntegration.integrationType}
                            defaultCallActivityTypeId={
                                clickToCallIntegration.defaultCallsActivityType &&
                                clickToCallIntegration.defaultCallsActivityType.id
                            }
                            defaultVoicemailActivityTypeId={
                                clickToCallIntegration.defaultVoicemailsActivityType &&
                                clickToCallIntegration.defaultVoicemailsActivityType.id
                            }
                            refetch={refetch}
                            onDisable={() => {
                                disableClickToCallIntegration()
                                    .then(() => {
                                        refetch();
                                    })
                                    .catch(() => {
                                        addNotification('Something went wrong', {
                                            body: 'Failed to disable click-to-call',
                                            icon: 'frowny',
                                        });
                                    });
                            }}
                            user={user}
                        />
                    )}
                    {enabledPhoneIntegrations.map((integration) => (
                        <SetupCard
                            key={integration.integrationType}
                            title={getServiceNoun(integration.integrationType)}
                            externalUserIdentifier={integration.externalUserIdentifier ?? ''}
                            tertiaryInformation={integration.tertiaryInformation ?? ''}
                            integrationType={integration.integrationType}
                            defaultCallActivityTypeId={integration.defaultCallsActivityType.id}
                            defaultVoicemailActivityTypeId={
                                integration.defaultVoicemailsActivityType &&
                                integration.defaultVoicemailsActivityType.id
                            }
                            refetch={refetch}
                            onDisable={() => handleDisable(integration.integrationType)}
                            user={user}
                        />
                    ))}
                    {!clickToCallIsEnabled && (
                        <>
                            <Body size='large' isBold={true} color='dark'>
                                Outbound calling, directly from Nutshell
                            </Body>
                            <PhoneIntegrationCard
                                requiresUpgrade={!canUseClickToCall}
                                icon={<NutshellLogoMarkIcon size={32} fill={colors.orange} />}
                                title={clickToCallTitle}
                                body='Make and log unlimited calls from your browser with AI-powered summaries and transcripts. '
                                onClick={() =>
                                    appHistory.push({
                                        pathname: window.location.pathname,
                                        search: window.location.search,
                                        hash: `#/connect-phone-integration/get-started?integrationType=CLICKTOCALL`,
                                    })
                                }
                            />
                        </>
                    )}
                    <Body size='large' isBold={true} color='dark'>
                        {clickToCallIsEnabled
                            ? 'Connect with your desktop phone application to start calling, logging, and tracking in Nutshell '
                            : 'Or connect with your phone system to start calling, logging, and tracking in Nutshell'}
                    </Body>
                    <GridList numColumns={2} isLoading={isLoadingUser} error={errorMessage}>
                        {disabledPhoneIntegrations.map((integration) => {
                            const Icon = getServiceTypeIconComponent(integration.integrationType);

                            return (
                                <PhoneIntegrationCard
                                    key={integration.integrationType}
                                    icon={<Icon size={32} />}
                                    title={getServiceNoun(integration.integrationType)}
                                    body={getServiceDescription(integration.integrationType)}
                                    onClick={() => {
                                        appHistory.push({
                                            pathname: window.location.pathname,
                                            search: window.location.search,
                                            hash: `#/connect-phone-integration/get-started?integrationType=${integration.integrationType}`,
                                        });
                                    }}
                                />
                            );
                        })}
                    </GridList>
                </div>
                <div className={styles['cue-cards-container']}>
                    {cueCards ? (
                        <CueCardStack cards={cueCards} />
                    ) : (
                        <LoadingText height={338} isFullWidth={true} />
                    )}
                </div>
            </div>
        </>
    );
}
